// Source: https://github.com/DavidHDev/react-bits/tree/main/src/ts-tailwind/Components/SpotlightCard (MIT + Commons Clause)
// Local change: trimmed to overlay-only. Paints a cursor-following radial tint over its parent element.
import { useEffect, useRef } from 'react';

type SpotlightCardProps = {
  color: string;
  size?: number;
  className?: string;
};

export default function SpotlightCard({ color, size = 320, className = '' }: SpotlightCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const overlay = ref.current;
    const parent = overlay?.parentElement;
    if (!overlay || !parent) return;

    let frame = 0;
    let x = 0;
    let y = 0;

    const paint = () => {
      frame = 0;
      overlay.style.setProperty('--x', `${x}px`);
      overlay.style.setProperty('--y', `${y}px`);
    };

    const onMove = (event: PointerEvent) => {
      const rect = parent.getBoundingClientRect();
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
      overlay.style.opacity = '1';
      if (!frame) frame = requestAnimationFrame(paint);
    };

    const onLeave = () => {
      overlay.style.opacity = '0';
    };

    parent.addEventListener('pointermove', onMove);
    parent.addEventListener('pointerleave', onLeave);
    return () => {
      parent.removeEventListener('pointermove', onMove);
      parent.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 transition-opacity duration-200 ${className}`}
      style={{
        opacity: 0,
        background: `radial-gradient(circle ${size}px at var(--x) var(--y), ${color}, transparent 70%)`
      }}
    />
  );
}
